import React, { useMemo } from "react";
import Customisetable from "../Components/Table/Customisetable";
import usePermissionDataStore from "../Zustand/Store/usePermissionDataStore";
import { checkPermission } from "./PermissionConfigrations";

/** Avoid classifying DD-MM-YYYY / YYYY-MM-DD as "phone" (only digits and hyphens). */
const looksLikeDateOnlyString = (s) => {
  const t = String(s ?? "").trim();
  if (!t) return false;
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return true;
  if (/^\d{2}-\d{2}-\d{4}$/.test(t)) return true;
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(t)) return true;
  return false;
};

// Helper function to infer data type from value
const inferDataType = (value) => {
  if (value instanceof Date) return 'date';
  if (typeof value === 'number') {
    // Check if it looks like currency (has decimal places)
    if (value.toString().includes('.')) return 'currency';
    return 'number';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 'string';
    // Check for email pattern
    if (value.includes('@')) return 'email';
    // Dates often match [0-9+\-() ] — treat as date, not phone
    if (looksLikeDateOnlyString(value)) return 'date';
    // Check for phone pattern (ignore whitespace-only placeholders)
    if (/^[0-9+\-() ]+$/.test(trimmed) && /\d/.test(trimmed)) return 'phone';
    return 'string';
  }
  return 'string';
};

// Default configuration for different field types
const fieldDefaults = {
  string: { width: 180, sortable: true, filterable: true },
  number: { width: 80, sortable: true, filterable: true },
  date: { width: 180, sortable: true, filterable: true },
  enum: { width: 100, sortable: false, filterable: true },
  email: { width: 180, sortable: false, filterable: true },
  phone: { width: 180, sortable: false, filterable: true },
  currency: { width: 180, sortable: true, filterable: true }
};

function TableDataGeneric({
  tableName,
  primaryKey,
  heading,
  data,
  sortname,
  Route,
  DeleteFunc,
  token,
  EditFunc,
  handleShow,
  linkType,
  onclickRow,
  configss,
  datagridKey = "",
  loadedBackendConfig = null,
  onSortChange,
  onPageChange,
  paginationData,
  ViewFunc,
  ReceiptFunc,
  EnrolledFunc,
  SnapshotFunc,
  LeadActivityFunc,
  AdmissionFunc,
  DemoParticipantFunc,
  FeePortalFunc,
  StudentAttendanceFunc,
  BatchAttendanceFunc,
  BatchClassesFunc,
  BatchStudentsFunc,
  BatchRecordsFunc,
  SendOfferMailFunc,
  showActions,
  showViewButton = true,
  showEditButton = true,
  showDeleteButton = true,
  showReceiptButton = false,
  showEnrolledButton = false,
  showSnapshotButton = false,
  showLeadActivityButton = false,
  showAdmissionButton = false,
  showDemoParticipantButton = false,
  showFeePortalButton = false,
  showStudentAttendanceButton = false,
  showBatchAttendanceButton = false,
  canShowBatchAttendanceForRow = null,
  showBatchClassesButton = false,
  showBatchStudentsButton = false,
  showBatchRecordsButton = false,
  showSendOfferMailButton = false,
  serverSearchEnabled = false,
  serverSearchValue = "",
  onServerSearchChange = null,
  onFilterChange = null,
  filterModule = null,
  CardData,
  toolbarCenterContent,
  searchPlaceholder,
  config = {},
  edit_delete_action,
  toggleActiveFn,
  loading: parentLoading = false,
  hideActiveFiltersBar = false,
  hideFilterButton = false,
  hidePersonalizeView = false,
  deleteRestoreOption = false,
  onStudentNameClick = undefined,
  onTrainingProgramNameClick = undefined,
}) {
  const { Permission } = usePermissionDataStore();

  const hasEditPermission = useMemo(() => {
    if (
      !edit_delete_action ||
      !Array.isArray(edit_delete_action) ||
      edit_delete_action.length === 0
    ) {
      return true;
    }
    const editPermission = edit_delete_action[0];
    return editPermission ? checkPermission(Permission, editPermission) : true;
  }, [edit_delete_action, Permission]);

  const hasDeletePermission = useMemo(() => {
    if (
      !edit_delete_action ||
      !Array.isArray(edit_delete_action) ||
      edit_delete_action.length < 2
    ) {
      return true;
    }
    const deletePermission = edit_delete_action[1];
    return deletePermission
      ? checkPermission(Permission, deletePermission)
      : false;
  }, [edit_delete_action, Permission]);

  const dataColumnKeys =
    Array.isArray(data) && data.length > 0 && data[0] && typeof data[0] === "object"
      ? Object.keys(data[0]).sort().join("\0")
      : "";

  const configuration = useMemo(() => {
    const buildFromConfigss = () => {
      if (!configss?.length) return null;
      return configss.map((col, index) => {
        const fieldKey = col.field || col.key;
        return {
          field: fieldKey,
          key: fieldKey,
          label: col.label,
          dataType: col.dataType || inferDataType(""),
          width: col.width || fieldDefaults.string.width,
          sortable: col.sortable !== false,
          filterable: col.filterable !== false,
          mandatory: !!col.required,
          defaultVisible: col.visible !== false,
          pinned: col.pinned === "left" || col.pinned === true,
          visible: col.visible !== false,
          order: col.order ?? index,
        };
      });
    };

    const generateColumns = (rows) => {
      if (!rows || rows.length === 0) return [];

      const mandatoryColumns = config.mandatoryColumns || [];
      const defaultVisibleColumns = config.defaultVisibleColumns || [];
      const pinnedColumns = config.pinnedColumns || [];
      const columnLabels = config.columnLabels || {};
      const columnWidths = config.columnWidths || {};

      const columns = Object.entries(rows[0] || {})
        .map(([key, value]) => {
          const lowerKey = key.toLowerCase();

          if (
            key.startsWith("__") ||
            key === "training_program_code" ||
            lowerKey.endsWith("_id") ||
            key === "id" ||
            lowerKey.includes("updated_at") ||
            lowerKey.includes("created_at") ||
            lowerKey.includes("created_by") ||
            typeof value === "object"
          ) {
            return null;
          }

          const dataType = inferDataType(value);
          const defaults = fieldDefaults[dataType];

          const autoLabel = key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          return {
            field: key,
            key,
            label: columnLabels[key] || autoLabel,
            dataType: inferDataType(value),
            width: columnWidths[key] ?? defaults.width,
            sortable: defaults.sortable,
            filterable: defaults.filterable,
            mandatory: mandatoryColumns.includes(key),
            defaultVisible: defaultVisibleColumns.includes(key),
            pinned: pinnedColumns.includes(key),
          };
        })
        .filter(Boolean);

      if (defaultVisibleColumns.length) {
        columns.sort((a, b) => {
          const ia = defaultVisibleColumns.indexOf(a.key);
          const ib = defaultVisibleColumns.indexOf(b.key);
          if (ia === -1 && ib === -1) return 0;
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        });
      }

      return columns;
    };

    const cols =
      buildFromConfigss() ||
      (config?.columns?.length ? config.columns : null) ||
      generateColumns(data);
    const defaultVisibleColumns =
      config.defaultVisibleColumns ||
      (configss?.length
        ? configss.filter((c) => c.visible).map((c) => c.field)
        : []);

    return [
      {
        table: tableName,
        primaryKey: primaryKey,
        heading: heading,
        lastModified: new Date().toISOString(),
        default_config: {
          columns: cols.map((col, index) => ({
            ...col,
            visible:
              defaultVisibleColumns.includes(col.field) || index < 5,
            order:
              col.field === "Employee_Code"
                ? 0
                : col.field === "Name"
                  ? 1
                  : defaultVisibleColumns.indexOf(col.field) ?? index,
          })),
          sortConfig: config.sort
            ? [{ key: config.sort.field, direction: config.sort.direction }]
            : [{ key: sortname, direction: "asc" }],
          rowsPerPage: config.pagination?.rowsPerPage || 25,
          filters: config.filters || {},
          search: config.search || {
            enabled: true,
            placeholder: "Search...",
            fields: [],
          },
        },
        user_config: {
          columns: cols.map((col, index) => ({
            ...col,
            key: col.key,
            field: col.field,
            visible:
              defaultVisibleColumns.includes(col.field) || index < 5,
            order:
              col.field === "Employee_Code"
                ? 0
                : col.field === "Name"
                  ? 1
                  : defaultVisibleColumns.indexOf(col.field) ?? index,
            width: col.width,
            sortDirection: null,
            filterValue: null,
            pinned:
              col.pinned ||
              col.field === "Employee_Code" ||
              col.field === "Name",
            dataType: col.dataType,
          })),
          sortConfig: config.sort
            ? [{ key: config.sort.field, direction: config.sort.direction }]
            : [{ key: sortname, direction: "asc" }],
          rowsPerPage: config.pagination?.rowsPerPage || 25,
          filters: config.filters || {},
          search: config.search || {
            enabled: true,
            placeholder: "Search...",
            fields: [],
          },
          lastModified: new Date().toISOString(),
        },
      },
    ];
  }, [data, dataColumnKeys, tableName, primaryKey, heading, sortname, config, configss]);

  const resolvedShowActions =
    showActions ??
    Boolean(
      showViewButton ||
        showEditButton ||
        showDeleteButton ||
        showReceiptButton ||
        showEnrolledButton ||
        showSnapshotButton ||
        showLeadActivityButton ||
        showAdmissionButton ||
        showDemoParticipantButton ||
        showFeePortalButton ||
        showStudentAttendanceButton ||
        showBatchAttendanceButton ||
        showBatchClassesButton ||
        showBatchStudentsButton ||
        showBatchRecordsButton ||
        showSendOfferMailButton
    );

  return (
    <Customisetable
      configuration={configuration}
      tableName={tableName}
      exportTitle={heading || tableName}
      Route={Route}
      DeleteFunc={hasDeletePermission ? DeleteFunc : undefined}
      EditFunc={hasEditPermission ? EditFunc : undefined}
      handleShow={handleShow}
      linkType={linkType}
      onclickRow={onclickRow}
      onSortChange={onSortChange}
      onPageChange={onPageChange}
      paginationData={paginationData}
      viewFunction={ViewFunc || handleShow}
      ReceiptFunc={ReceiptFunc}
      EnrolledFunc={EnrolledFunc}
      SnapshotFunc={SnapshotFunc}
      LeadActivityFunc={LeadActivityFunc}
      AdmissionFunc={AdmissionFunc}
      DemoParticipantFunc={DemoParticipantFunc}
      FeePortalFunc={FeePortalFunc}
      StudentAttendanceFunc={StudentAttendanceFunc}
      BatchAttendanceFunc={BatchAttendanceFunc}
      BatchClassesFunc={BatchClassesFunc}
      BatchStudentsFunc={BatchStudentsFunc}
      BatchRecordsFunc={BatchRecordsFunc}
      SendOfferMailFunc={SendOfferMailFunc}
      token={token}
      mainKey={primaryKey}
      recordKey={primaryKey}
      recordLabel="Record"
      mainData={data}
      showActions={resolvedShowActions}
      showViewButton={showViewButton}
      showEditButton={hasEditPermission && showEditButton}
      showDeleteButton={hasDeletePermission && showDeleteButton}
      showReceiptButton={showReceiptButton}
      showEnrolledButton={showEnrolledButton}
      showSnapshotButton={showSnapshotButton}
      showLeadActivityButton={showLeadActivityButton}
      showAdmissionButton={showAdmissionButton}
      showDemoParticipantButton={showDemoParticipantButton}
      showFeePortalButton={showFeePortalButton}
      showStudentAttendanceButton={showStudentAttendanceButton}
      showBatchAttendanceButton={showBatchAttendanceButton}
      canShowBatchAttendanceForRow={canShowBatchAttendanceForRow}
      showBatchClassesButton={showBatchClassesButton}
      showBatchStudentsButton={showBatchStudentsButton}
      showBatchRecordsButton={showBatchRecordsButton}
      showSendOfferMailButton={showSendOfferMailButton}
      serverSearchEnabled={serverSearchEnabled}
      serverSearchValue={serverSearchValue}
      onServerSearchChange={onServerSearchChange}
      onFilterChange={onFilterChange}
      filterModule={filterModule}
      configss={configss}
      loadedBackendConfig={loadedBackendConfig}
      datagridKey={datagridKey}
      toolbarCenterContent={toolbarCenterContent}
      searchPlaceholder={searchPlaceholder}
      CardColoumn={CardData}
      primaryFilds={null}
      toggleActiveFn={toggleActiveFn}
      parentLoading={parentLoading}
      hideActiveFiltersBar={hideActiveFiltersBar}
      hideFilterButton={hideFilterButton}
      hidePersonalizeView={hidePersonalizeView}
      onStudentNameClick={onStudentNameClick}
      onTrainingProgramNameClick={onTrainingProgramNameClick}
      deleteRestoreOption={deleteRestoreOption}
    />
  );
}
export default TableDataGeneric;